const CACHE_KEY = 'aceb_data_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

interface CacheData {
  categories?: string[];
  countries?: string[];
  timestamp: number;
}

class DataCache {
  private isClient = typeof window !== 'undefined';

  private getFromStorage(): CacheData | null {
    if (!this.isClient) return null;
    
    try {
      const data = localStorage.getItem(CACHE_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  private saveToStorage(data: CacheData): void {
    if (!this.isClient) return;
    
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
      // Storage full or disabled
    }
  }

  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > CACHE_EXPIRY;
  }

  clearCache(): void {
    if (!this.isClient) return;
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // Storage disabled
    }
  }

  getCategories(): string[] | null {
    const cache = this.getFromStorage();
    if (cache?.categories && !this.isExpired(cache.timestamp)) {
      // Validate that cached categories are strings, not objects
      if (Array.isArray(cache.categories) && cache.categories.every(cat => typeof cat === 'string')) {
        return cache.categories;
      } else {
        // Invalid format, clear cache
        this.clearCache();
        return null;
      }
    }
    return null;
  }

  setCategories(categories: string[]): void {
    const cache = this.getFromStorage() || { timestamp: Date.now() };
    cache.categories = categories;
    cache.timestamp = Date.now();
    this.saveToStorage(cache);
  }

  getCountries(): string[] | null {
    const cache = this.getFromStorage();
    if (cache?.countries && !this.isExpired(cache.timestamp)) {
      return cache.countries;
    }
    return null;
  }

  setCountries(countries: string[]): void {
    const cache = this.getFromStorage() || { timestamp: Date.now() };
    cache.countries = countries;
    cache.timestamp = Date.now();
    this.saveToStorage(cache);
  }

}

export const dataCache = new DataCache();
